import { useState } from "react";

import {
  ADMIN_TAB_NAME,
  dispatchCompareCostInvalidate,
  dispatchWorkForChanged,
  getApiErrorMessage,
  postWorkFor,
  runtimeEnv,
  workForDisplay,
  writeStoredWorkFor,
} from "@penn/web-common";
import { PennText } from "@/components/ui";
import { useWorkFor } from "@/hooks/useWorkFor";

/** Staff-host URL for choosing a customer, or null when not configured. */
function adminWorkForUrl(base: string | undefined): string | null {
  if (!base) return null;
  try {
    // /work-for, not /admin: the console needs permissions sales lacks, so
    // that link stranded them. Parsed, not concatenated, so a malformed value
    // can't become a `javascript:` href.
    const url = new URL("/work-for", base);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

type WorkForStatusProps = {
  onApplied?: () => void;
};

// Read-only: customers are chosen and staged in the admin console only.
export function WorkForStatus({ onApplied }: WorkForStatusProps) {
  const target = workForDisplay(useWorkFor());
  const switchUrl = adminWorkForUrl(runtimeEnv("VITE_WEB_ADMIN_URL"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const workAsSelf = async () => {
    setError(null);
    setBusy(true);
    try {
      await postWorkFor({});
      writeStoredWorkFor({ kind: "self" });
      dispatchCompareCostInvalidate();
      dispatchWorkForChanged();
      onApplied?.();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to update Work-For"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="px-4 py-3 border-b border-gray-100"
      onClick={(e) => e.stopPropagation()}
    >
      <PennText
        as="p"
        size="xs"
        color="blue_grey"
        font="semibold"
        className="uppercase text-slate-500"
      >
        Working for
      </PennText>
      <p
        className="mt-1 text-sm font-semibold text-slate-900 truncate"
        data-testid="work-for-current"
      >
        {target ? target.email : "Myself"}
      </p>
      {target && (target.name || target.company) && (
        <p className="text-xs text-slate-500 truncate">
          {[target.name, target.company].filter(Boolean).join(" / ")}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        {switchUrl ? (
          <a
            href={switchUrl}
            // Own site, so no noopener: it must be able to reuse the admin tab.
            target={ADMIN_TAB_NAME}
            className="text-xs font-semibold text-penn-600 hover:text-penn-700 underline"
          >
            {target ? "Switch customer" : "Work as a customer"}
          </a>
        ) : (
          <span className="text-xs text-slate-500">
            Switch customers on the staff host.
          </span>
        )}
        {target && (
          <button
            type="button"
            onClick={() => void workAsSelf()}
            disabled={busy}
            className="text-xs font-semibold text-slate-600 hover:text-slate-900 underline disabled:opacity-50"
          >
            {busy ? "Switching…" : "Work as myself"}
          </button>
        )}
      </div>
      {error && (
        <PennText as="p" size="xs" color="error" className="mt-1">
          {error}
        </PennText>
      )}
    </div>
  );
}
